import i18n, { type PostProcessorModule } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { isRecord, parseString } from 'utils/typeGuards';

import { registerChunkErrorTranslator } from './chunkErrorFallbackText';
import en from './locales/en/common.json';
import {
  candidateLocaleFallbacks,
  englishCommonCodes,
  getLocaleBundles,
  markInitialLocaleRegistered,
  needsLocaleFetch,
  normalizeLangParam,
  supportedLanguages,
  type LocaleBundle,
} from './i18nLocaleLoader';

export { supportedLanguages };

const resolveInitialLanguage = (): string => {
  if (typeof window === 'undefined') return 'en';
  const lang = new URLSearchParams(window.location.search).get('lang');
  if (lang) {
    return normalizeLangParam(lang);
  }
  return 'en';
};

const initialLanguage = resolveInitialLanguage();

/** Where a translation came from, as i18next resolved it. */
export interface ResolvedTranslation {
  /** The requested key (the last one tried, for a key list). */
  key: string;
  /** The language the text was asked for. */
  language: string;
  /** The language it was found in; for a key found nowhere, the last one tried. */
  usedLanguage: string | undefined;
  /** The namespace it was found in, or the last one tried. */
  usedNamespace: string | undefined;
  /** False when no language had the key (the key or a default was shown). */
  found: boolean;
}

export type ResolvedTranslationListener = (
  translation: ResolvedTranslation
) => void;

let resolvedTranslationListener: ResolvedTranslationListener | null = null;

/**
 * Lets an app watch where each translation resolved (the recruiter app reports
 * keys a non-English language is missing). Nothing listens by default.
 */
export const registerResolvedTranslationListener = (
  listener: ResolvedTranslationListener | null
): void => {
  resolvedTranslationListener = listener;
};

/**
 * Reads the resolution i18next passes post-processors under
 * `postProcessPassResolved`; text is returned unchanged.
 */
const resolvedTranslationProcessor: PostProcessorModule = {
  type: 'postProcessor',
  name: 'resolvedTranslationListener',
  process(value, key, options) {
    const listener = resolvedTranslationListener;
    if (!listener) return value;
    const resolved: unknown = isRecord(options)
      ? options.i18nResolved
      : undefined;
    if (!isRecord(resolved)) return value;
    const requestedKey = Array.isArray(key) ? key[key.length - 1] : key;
    try {
      listener({
        key: parseString(resolved.usedKey) ?? requestedKey ?? '',
        language: parseString(options.lng) ?? i18n.language,
        usedLanguage: parseString(resolved.usedLng),
        usedNamespace: parseString(resolved.usedNS),
        found: resolved.res !== undefined,
      });
      // swallow-ok: a listener failure must never break the text being rendered
    } catch {
      // Translation keeps working; the listener only observes.
    }
    return value;
  },
};

// The recruiter app (the build without REACT_APP_SALV_APP) reads `common` only
// in shared components inside its pages and the Report Issue modal, so English
// `common` is a lazy chunk there that ensureEnglishCommon registers before any
// of them renders, instead of riding the app shell. The candidate and internal
// apps render candidate text from their first paint and keep it bundled, as do
// tests, which render components outside any page loader.
const LAZY_ENGLISH_COMMON =
  !import.meta.env.REACT_APP_SALV_APP && import.meta.env.MODE !== 'test';

void i18n
  .use(initReactI18next)
  .use(resolvedTranslationProcessor)
  .init({
    // English is the only locale in the eager bundle (outside the recruiter app);
    // the others are fetched on demand as their own chunks (see
    // i18nLocaleLoader.ts).
    resources: LAZY_ENGLISH_COMMON
      ? {}
      : Object.fromEntries(
          englishCommonCodes.map((code) => [code, { common: en }])
        ),
    lng: initialLanguage,
    fallbackLng: candidateLocaleFallbacks,
    ns: ['common'],
    defaultNS: 'common',
    // Every locale file is flat, with dots inside the keys ("common.cancel").
    keySeparator: false,
    interpolation: { escapeValue: false },
    // Hands resolvedTranslationProcessor where each text was found.
    postProcess: [resolvedTranslationProcessor.name],
    postProcessPassResolved: true,
    initImmediate: false,
    partialBundledLanguages: true,
  });

// The chunk-load fallback renders outside this module's import graph so the
// entry chunk stays i18next-free; hand it the translator now that i18n exists.
registerChunkErrorTranslator((key, defaultValue) =>
  i18n.t(key, { defaultValue })
);

/**
 * Registers fetched `common` bundles, keeping any already registered. Also
 * used by the recruiter app, whose shared components read `common`.
 */
export const registerCommonBundles = (bundles: LocaleBundle[]): void => {
  for (const { code, bundle } of bundles) {
    if (!i18n.hasResourceBundle(code, 'common')) {
      // The loader's objects are never mutated, so i18next need not copy them.
      i18n.addResourceBundle(code, 'common', bundle, false, false, {
        skipCopy: true,
      });
    }
  }
};

let englishCommon: Promise<void> | null = null;

/**
 * Registers English `common` where it is not bundled (the recruiter app) and
 * resolves once it is; resolves at once elsewhere. The recruiter router holds
 * every page on it, so shared components never render raw keys. A failed fetch
 * rejects, so the caller's chunk-error recovery applies, and a later call
 * retries.
 */
export const ensureEnglishCommon = (): Promise<void> => {
  if (!LAZY_ENGLISH_COMMON) {
    return Promise.resolve();
  }
  if (!englishCommon) {
    englishCommon = import('./locales/en/common.json').then(
      ({ default: bundle }) =>
        registerCommonBundles(
          englishCommonCodes.map((code) => ({ code, bundle }))
        )
    );
    englishCommon.catch(() => {
      englishCommon = null;
    });
  }
  return englishCommon;
};

const registerLocaleBundles = async (lang: string): Promise<void> => {
  registerCommonBundles(await getLocaleBundles(lang));
};

/**
 * Fetches the locale bundle (if needed) before switching languages. On fetch
 * failure the switch still happens and i18next falls back to bundled English,
 * so candidates see English rather than raw translation keys.
 */
export const loadAndChangeLanguage = async (lang: string): Promise<unknown> => {
  const normalized = normalizeLangParam(lang);
  if (needsLocaleFetch(normalized)) {
    try {
      await registerLocaleBundles(normalized);
    } catch {
      // swallow-ok: Failed candidate locale fetches are evicted for retry; changeLanguage uses registered English fallback and releases the initial render gate.
      // The loader drops failed fetches from its cache, so a later language
      // switch can retry.
    }
  }
  return i18n.changeLanguage(normalized);
};

// A non-English ?lang= needs its bundle registered before the language is
// re-applied; the entry's prefetchLocale has usually already warmed the chunk.
// Both branches signal the entry, which holds its first render until then so
// the candidate does not see English swap to their language a beat later.
// loadAndChangeLanguage swallows fetch failures and still changes language, so
// the finally fires on success and failure alike and a dead locale chunk can
// never wedge that gate.
if (needsLocaleFetch(initialLanguage)) {
  void loadAndChangeLanguage(initialLanguage).finally(
    markInitialLocaleRegistered
  );
} else {
  markInitialLocaleRegistered();
}

export default i18n;
